import React, { useState } from "react";
import { CDDCheck, ChecksLogProps } from "../../../../types/cdd-records.types";
import {
    formatDateShort,
    formatDateTime,
} from "../../../../utils/dateFormatters";
import { CheckDetailModal } from "./CheckDetailModal";
import {
    checkTypeLabels,
    getResultBadgeClass,
    getStatusBadgeClass,
} from "./checksLogConfig";

export const ChecksLog: React.FC<ChecksLogProps> = ({
  checksLog,
  onAddCheck,
  onViewCheck,
  recordVerificationStatus,
}) => {
  const isRecordVerified = recordVerificationStatus === "verified";

  // When the record is verified, checks with review-required or pending results were accepted as part of verification
  const getEffectiveResult = (result?: string) => {
    if (!isRecordVerified) return result;
    if (result === "review-required") return "reviewed-cleared";
    if (result === "pending") return "accepted";
    return result;
  };

  const [filter, setFilter] = useState<
    "all" | "completed" | "pending" | "failed"
  >("all");
  const [expandedCheck, setExpandedCheck] = useState<string | null>(null);
  const [selectedCheck, setSelectedCheck] = useState<CDDCheck | null>(null);

  const filteredChecks = checksLog.checks.filter((check) => {
    if (filter === "all") return true;
    if (filter === "completed") return check.status === "completed";
    if (filter === "pending")
      return check.status === "pending" || check.status === "in-progress";
    if (filter === "failed") return check.status === "failed";
    return true;
  });

  const completedChecks = checksLog.checks.filter(
    (c) => c.status === "completed",
  ).length;
  const pendingChecks = checksLog.checks.filter(
    (c) => c.status === "pending" || c.status === "in-progress",
  ).length;
  const failedChecks = checksLog.checks.filter(
    (c) => c.status === "failed",
  ).length;
  const reviewAcceptedCount = isRecordVerified
    ? checksLog.checks.filter(
        (c) => c.result === "review-required" || c.result === "pending",
      ).length
    : 0;

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg border border-slate-200/60 overflow-hidden">
      {/* Header with gradient */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white">Checks Log</h3>
            <p className="text-sm text-slate-300 mt-1">
              Track all CDD checks and their outcomes
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Compliance Status Badge */}
            <span
              className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                checksLog.complianceStatus === "compliant"
                  ? "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/30"
                  : checksLog.complianceStatus === "non-compliant"
                    ? "bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/30"
                    : "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/30"
              }`}
            >
              {checksLog.complianceStatus === "compliant"
                ? "Compliant"
                : checksLog.complianceStatus === "non-compliant"
                  ? "Non-Compliant"
                  : "Review Required"}
            </span>
            {onAddCheck && !isRecordVerified && (
              <button
                onClick={onAddCheck}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Check
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Verified + flagged checks banner */}
      {isRecordVerified && reviewAcceptedCount > 0 && (
        <div className="px-6 py-3 bg-teal-50 border-b border-teal-200 flex items-center gap-3">
          <svg className="w-5 h-5 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-teal-800">
            <span className="font-semibold">{reviewAcceptedCount} check result{reviewAcceptedCount !== 1 ? "s were" : " was"} reviewed and accepted</span> as part of the verification process.
          </p>
        </div>
      )}

      {/* Summary Stats - Vibrant Gradient Cards */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 border-b border-slate-200/60">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Total Checks - Slate to Gray */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-slate-500 via-slate-600 to-gray-700 rounded-2xl p-3 sm:p-4 border border-slate-400/30 shadow-lg shadow-slate-500/25">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-black text-white drop-shadow-lg">
                    {checksLog.checks.length}
                  </p>
                  <p className="text-xs font-semibold text-slate-200 mt-1">
                    Total Checks
                  </p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Completed - Emerald to Teal */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl p-3 sm:p-4 border border-emerald-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-black text-white drop-shadow-lg">
                    {completedChecks}
                  </p>
                  <p className="text-xs font-semibold text-emerald-100 mt-1">
                    Completed
                  </p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Pending - Blue to Indigo */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl p-3 sm:p-4 border border-blue-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-black text-white drop-shadow-lg">
                    {pendingChecks}
                  </p>
                  <p className="text-xs font-semibold text-blue-100 mt-1">
                    Pending
                  </p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Failed - Rose to Red */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-rose-500 via-rose-600 to-red-700 rounded-2xl p-3 sm:p-4 border border-rose-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-black text-white drop-shadow-lg">
                    {failedChecks}
                  </p>
                  <p className="text-xs font-semibold text-rose-100 mt-1">
                    Failed
                  </p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Outstanding - Purple to Pink */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 rounded-2xl p-3 sm:p-4 border border-purple-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-black text-white drop-shadow-lg">
                    {checksLog.outstandingChecks?.length || 0}
                  </p>
                  <p className="text-xs font-semibold text-purple-100 mt-1">
                    Outstanding
                  </p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Outstanding Checks Alert */}
      {checksLog.outstandingChecks &&
        checksLog.outstandingChecks.length > 0 && (
          <div className="px-6 py-4 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-200/60">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-orange-600 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <h4 className="font-medium text-orange-800">
                  Outstanding Checks Due
                </h4>
                <ul className="mt-2 space-y-1">
                  {checksLog.outstandingChecks.map((oc, index) => (
                    <li
                      key={index}
                      className="text-sm text-orange-700 flex items-center justify-between"
                    >
                      <span>
                        {checkTypeLabels[oc.checkType]?.label || oc.checkType}
                      </span>
                      <span className="text-xs">
                        Due: {formatDateShort(oc.dueDate)}
                        <span
                          className={`ml-2 px-1.5 py-0.5 rounded ${
                            oc.priority === "critical"
                              ? "bg-red-200 text-red-800"
                              : oc.priority === "high"
                                ? "bg-orange-200 text-orange-800"
                                : oc.priority === "medium"
                                  ? "bg-yellow-200 text-yellow-800"
                                  : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          {oc.priority}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

      {/* Filter Tabs */}
      <div className="px-6 border-b border-slate-200/60">
        <div className="flex space-x-4">
          {(["all", "completed", "pending", "failed"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                filter === f
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {f}{" "}
              {f !== "all" && (
                <span className="ml-1 text-xs text-gray-400">
                  (
                  {f === "completed"
                    ? completedChecks
                    : f === "pending"
                      ? pendingChecks
                      : failedChecks}
                  )
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Checks List */}
      <div className="divide-y divide-slate-200/60">
        {filteredChecks.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 mb-4">
              <svg
                className="h-8 w-8 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <p className="text-slate-500 font-medium">
              No checks found matching your filter criteria.
            </p>
          </div>
        ) : (
          filteredChecks.map((check) => {
            const typeConfig =
              checkTypeLabels[check.checkType] || checkTypeLabels["other"];
            const isExpanded = expandedCheck === check.id;

            return (
              <div
                key={check.id}
                className="hover:bg-gradient-to-r hover:from-slate-50/50 hover:to-transparent transition-all duration-200"
              >
                <div
                  className="px-4 sm:px-6 py-4 sm:py-5 cursor-pointer"
                  onClick={() => setExpandedCheck(isExpanded ? null : check.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`p-2.5 rounded-xl shadow-sm ${
                          check.status === "completed"
                            ? "bg-gradient-to-br from-emerald-50 to-green-50 text-emerald-600 ring-1 ring-emerald-200"
                            : check.status === "in-progress"
                              ? "bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 ring-1 ring-blue-200"
                              : check.status === "failed"
                                ? "bg-gradient-to-br from-rose-50 to-red-50 text-rose-600 ring-1 ring-rose-200"
                                : "bg-gradient-to-br from-slate-50 to-gray-50 text-slate-600 ring-1 ring-slate-200"
                        }`}
                      >
                        {typeConfig.icon}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">
                          {typeConfig.label}
                          {/* AL-482: names the subject of the check. Several identity checks on one
                              case are otherwise indistinguishable. */}
                          {check.subject && (
                            <span className="font-normal text-slate-500">
                              {" "}— {check.subject}
                            </span>
                          )}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {formatDateTime(check.checkDate)} •{" "}
                          {check.performedBy}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeClass(check.status)}`}
                      >
                        {check.status}
                      </span>
                      {check.result && (() => {
                        const effective = getEffectiveResult(check.result);
                        const badgeClass =
                          effective === "reviewed-cleared"
                            ? "bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-800 shadow-sm"
                            : effective === "accepted"
                              ? "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 shadow-sm"
                              : getResultBadgeClass(check.result);
                        const label =
                          effective === "reviewed-cleared"
                            ? "Reviewed & Cleared"
                            : effective === "accepted"
                              ? "Accepted"
                              : check.result.replace("-", " ");
                        return (
                          <span className={`px-2 py-1 text-xs font-medium rounded ${badgeClass}`}>
                            {label}
                          </span>
                        );
                      })()}
                      <svg
                        className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 pb-5 bg-gradient-to-br from-slate-50/80 via-white/50 to-slate-50/80">
                    <div className="pt-5 border-t border-slate-200/60 space-y-3">
                      {check.details && (
                        <div>
                          <span className="text-xs text-gray-500">Details</span>
                          <p className="text-sm text-gray-700">
                            {check.details}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {check.relatedEntityType && (
                          <div>
                            <span className="text-xs text-gray-500 block">
                              Related Entity
                            </span>
                            <span className="text-sm text-gray-900">
                              {check.relatedEntityType}
                            </span>
                          </div>
                        )}
                        {check.expiryDate && (
                          <div>
                            <span className="text-xs text-gray-500 block">
                              Expiry Date
                            </span>
                            <span className="text-sm text-gray-900">
                              {formatDateShort(check.expiryDate)}
                            </span>
                          </div>
                        )}
                      </div>

                      {check.documentReferences &&
                        check.documentReferences.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-500 block mb-1">
                              Related Documents
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {check.documentReferences.map((doc, index) => (
                                <span
                                  key={index}
                                  className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded"
                                >
                                  {doc}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                      {check.notes && (
                        <div className="p-3 bg-yellow-50 rounded text-sm text-yellow-800">
                          <strong>Note:</strong> {check.notes}
                        </div>
                      )}

                      {/* View Full Details button - only show if there are additional details beyond what's shown inline */}
                      {(check.details || check.notes || (check.documentReferences && check.documentReferences.length > 0)) && (
                        <div className="pt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCheck(check);
                            }}
                            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            View Full Details
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer with next scheduled check */}
      {checksLog.nextScheduledCheck && (
        <div className="px-6 py-3 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 border-t border-slate-200/60 text-sm text-slate-600">
          <span className="font-medium">Next Scheduled Check:</span>{" "}
          {formatDateShort(checksLog.nextScheduledCheck)}
        </div>
      )}

      {/* Check Detail Modal */}
      {selectedCheck && (
        <CheckDetailModal
          check={selectedCheck}
          typeConfig={
            checkTypeLabels[selectedCheck.checkType] || checkTypeLabels["other"]
          }
          onClose={() => setSelectedCheck(null)}
        />
      )}
    </div>
  );
};

export default ChecksLog;
